import React from "react";
import { connect } from "react-redux";
import { ListItem } from "../List";
import { Button } from "../Button";
import language from "../../language";
import { getStorage, setStorage } from "../../utils/storage";
import { BTD } from "../../utils/loginPortal";
import fetchBloodCenters from "../../utils/queries/bloodCenterQuery";
import { Loading } from "../Loader";
import toastMessage from "../../utils/toastMessage";
import api from "../../utils/api";
import { getDefaultPage } from "../../utils/handleAccessRoles";

class Workspace extends React.Component {
  state = {
    user: {},
    isUpdating: "",
    loading: false,
    isFetchingHospitals: false,
  };

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });
    await this.fetchHospitals(user);
    await this.resolveCentreNames();
  };

  /**
   * Put a name on every centre, whatever shape it arrived in.
   *
   * `center` can hold populated documents or bare ObjectIds, depending on
   * which login path wrote the session and whether the account fetch was
   * allowed. Bare ids rendered as "(Unnamed Centre)" six times over, and a
   * chooser of identical unnamed rows is not a choice.
   *
   * The centre list is read as a dropdown, which every signed-in account
   * may do - unlike /account, which an ordinary user can be refused.
   */
  resolveCentreNames = async () => {
    const { user } = this.state;
    const centres = user?.centers || user?.center || [];

    if (!centres.length || centres.every((el) => el?.name)) return;

    try {
      const all = await fetchBloodCenters(this.props.defaultLanguage, {
        type: "dropdown",
      });

      if (!Array.isArray(all) || !all.length) return;

      const byId = new Map(
        all.map((el) => [String(el.value), el.label || el.name]),
      );

      const named = centres.map((el) => {
        const id = String(el?._id || el?.value || el);

        return el?.name ? el : { _id: id, name: byId.get(id) };
      });

      this.setState((prev) => ({ user: { ...prev.user, center: named } }));
    } catch (e) {
      // Leave the fallback labels rather than blocking the chooser.
    }
  };

  fetchHospitals = async (user) => {
    if (!user?.id && !user?.token) return;
    try {
      this.setState({ isFetchingHospitals: true });
      const res = await api({
        method: "GET",
        url: "/account",
        params: { id: user.id },
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const accounts = Array.isArray(res.data) ? res.data : res.data?.data;
      const account = accounts?.[0];

      // Take both lists from the fetched account, not from storage. The
      // account endpoint populates them, so the names are there; storage
      // can hold bare ids, which rendered as rows with no label at all -
      // a workspace chooser offering six blank lines to pick between.
      this.setState((prev) => ({
        user: {
          ...prev.user,
          hospitals: account?.hospitals?.length
            ? account.hospitals
            : prev.user?.hospitals,
          center: account?.center?.length ? account.center : prev.user?.center,
        },
      }));
    } catch (_) {
      // fall back to whatever is already in state from sessionStorage
    } finally {
      this.setState({ isFetchingHospitals: false });
    }
  };

  /**
   * BTD staff pick a centre, not a hospital.
   *
   * The whole list is kept on the account as `centers` so the switcher can
   * be reopened later; `center` is narrowed to the one chosen, because that
   * is the field every query scopes on.
   */
  handleProceedCenter = async (center) => {
    try {
      this.setState({ loading: true, isUpdatingThisItem: center?.name });

      const { user } = this.state;
      const all = user.centers || user.center || [];

      await setStorage({
        ...user,
        centers: all,
        center: [center],
        centerName: center?.name || center?.code,
      });

      window.location.href = "/dashboard/donors";
    } catch (error) {
      console.log(error);
      toastMessage("error", error);
      this.setState({ loading: false });
    }
  };

  handleProceed = async (hospital) => {
    try {
      this.setState({ loading: true });
      let { user } = this.state;

      user.hospital = hospital;
      user.stock = hospital?.bloodBank?._id;
      user.stockName = hospital?.bloodBank?.name;

      await setStorage(user);

      const options = {
        method: "PUT",
        url: "/account",
        data: {
          id: user.id,
          hospital: hospital?._id || hospital,
          stock: hospital?.bloodBank?._id,
          stockName: hospital?.bloodBank?.name,
        },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };

      await api(options);

      await this.onUpdateToken(hospital);
    } catch (error) {
      toastMessage("error", error);

      this.setState({
        loading: false,
      });
    }
  };

  onUpdateToken = async (hospital) => {
    try {
      let url = `/account/switch`,
        method = "POST",
        user = await getStorage();

      let requestedBody = {
        id: user.id,
        hospital: hospital?._id || hospital,
        hospitalName: hospital?.name,
        newStock: hospital?.bloodBank?._id,
        stockName: hospital?.bloodBank?.name,
      };

      const options = {
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
        data: requestedBody,
      };

      const data = await api(options);

      this.setState({
        isSubmitting: false,
      });

      toastMessage(
        "success",
        language[this.props.defaultLanguage][
          method === "PUT" ? "update_account_success" : "add_account_success"
        ]
      );

      user.hospital = hospital;
      user.hospitalName = hospital?.name;
      user.stock = hospital?.bloodBank?._id;
      user.stockName = hospital?.bloodBank?.name;
      user.token = data.data.token;

      await setStorage(user);

      this.setState({ loading: false });

      const defaultPage = getDefaultPage();

      if (defaultPage) {
        return (window.location.href = defaultPage);
      }

      window.location.href = "/dashboard/donors";
    } catch (error) {
      console.log(error);
      toastMessage("error", error);

      this.setState({ loading: false });
    }
  };

  render() {
    const { user } = this.state;
    const centres = user?.centers || user?.center || [];
    const hasHospitals = this.state.user?.hospitals?.length > 0;

    const isCentreStaff = user?.loginPortal === BTD;

    return (
      <div className="col-md-4 offset-md-4 mt-4">
        <div className="card">
          <div className="card-header" style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", left: "1rem" }}>
              <Button
                text={language[this.props.defaultLanguage].go_back}
                icon="arrow_back"
                className="btn-danger"
                styles={{ padding: "1px 10px", fontSize: "0.78rem", lineHeight: "1.4", height: "28px" }}
                onPress={() => window.history.back()}
              />
            </div>
            <h3 className="mb-0">{language[this.props.defaultLanguage].choose_work_place}</h3>
          </div>
          <div className="card-body">
            {(this.state.loading || this.state.isFetchingHospitals) && (
              <center>
                <Loading />
              </center>
            )}
            {!this.state.isFetchingHospitals &&
              !this.state.loading &&
              (isCentreStaff
                ? centres.length === 0
                : !this.state.user?.hospitals ||
                  this.state.user.hospitals.length === 0) && (
                <p className="text-center text-muted">
                  {language[this.props.defaultLanguage].no_workspaces ||
                    "No workspaces assigned to your account."}
                </p>
              )}
            {isCentreStaff
              ? centres.map((el, i) => (
                  <ListItem
                    key={i}
                    name={el?.name || el?.code || "(unnamed centre)"}
                    rightButton={
                      language[this.props.defaultLanguage].continue
                    }
                    onPress={() => this.handleProceedCenter(el)}
                    icon="apartment"
                    height={55}
                    isUpdating={this.state.isUpdatingThisItem === el.name}
                  />
                ))
              : this?.state?.user?.hospitals?.map((el, i) => (
                  <ListItem
                    key={i}
                    name={el?.name || el?.code || "(unnamed facility)"}
                    rightButton={
                      language[this.props.defaultLanguage].continue
                    }
                    onPress={() => this.handleProceed(el)}
                    icon="domain"
                    height={55}
                    isUpdating={this.state.isUpdatingThisItem === el.name}
                  />
                ))}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(Workspace);
